<template>
  <v-dialog v-model="dialog" fullscreen hide-overlay transition="dialog-bottom-transition" content-class="help-center-dialog">
    <v-card class="help-center-card">
      <div class="aurora-bg"></div>

      <!-- Toolbar -->
      <v-toolbar flat color="transparent" class="glass-toolbar">
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title class="font-weight-black text-h6 pl-0">
          <span class="gradient-text">Help Center</span>
        </v-toolbar-title>
        <v-spacer></v-spacer>
      </v-toolbar>

      <!-- Content Container -->
      <div class="content-container pa-4">
        <!-- Search Header -->
        <div class="text-center mb-6">
          <h2 class="text-h4 font-weight-bold white--text mb-2">Wie können wir helfen?</h2>
          <v-text-field
            v-model="searchQuery"
            placeholder="Suche nach Themen (z.B. 'Statistik', 'Plan bearbeiten')"
            solo
            flat
            background-color="rgba(255,255,255,0.1)"
            prepend-inner-icon="mdi-magnify"
            rounded
            clearable
            class="glass-search"></v-text-field>
        </div>

        <!-- Categories (Horizontal Scroll) -->
        <div class="categories-scroll mb-6" v-if="!searchQuery">
          <div
            v-for="cat in categories"
            :key="cat.id"
            class="category-chip"
            :class="{ active: selectedCategory === cat.id }"
            @click="selectedCategory = cat.id">
            <v-icon small class="mr-2" :color="selectedCategory === cat.id ? 'white' : 'grey lighten-1'">{{ cat.icon }}</v-icon>
            {{ cat.name }}
          </div>
        </div>

        <!-- Articles List -->
        <v-slide-y-transition group tag="div">
          <div v-for="(article, index) in filteredArticles" :key="article.id" class="mb-3">
            <v-card class="article-card" @click="toggleArticle(article.id)">
              <div class="d-flex align-center pa-4">
                <v-avatar size="40" :color="article.color" class="mr-3 elevation-5" style="opacity: 0.9">
                  <v-icon small color="white">{{ article.icon }}</v-icon>
                </v-avatar>
                <div class="flex-grow-1">
                  <div class="font-weight-bold white--text text-subtitle-1">{{ article.title }}</div>
                  <div class="caption grey--text text--lighten-1">{{ article.subtitle }}</div>
                </div>
                <v-icon :class="{ 'rotate-180': expandedArticle === article.id }" color="grey">mdi-chevron-down</v-icon>
              </div>

              <v-expand-transition>
                <div v-if="expandedArticle === article.id">
                  <v-divider class="mx-4" style="border-color: rgba(255, 255, 255, 0.1)"></v-divider>
                  <div class="pa-4 pt-2 body-2 grey--text text--lighten-2 article-content">
                    <div v-html="article.content"></div>
                  </div>
                </div>
              </v-expand-transition>
            </v-card>
          </div>
        </v-slide-y-transition>

        <!-- Empty State -->
        <div v-if="filteredArticles.length === 0" class="text-center mt-10">
          <v-icon size="60" color="grey darken-2">mdi-text-search</v-icon>
          <p class="grey--text mt-2">Keine Artikel gefunden.</p>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "HelpCenter",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchQuery: "",
      selectedCategory: "all",
      expandedArticle: null,
      categories: [
        { id: "all", name: "Alle", icon: "mdi-apps" },
        { id: "basics", name: "Grundlagen", icon: "mdi-school" },
        { id: "workout", name: "Training", icon: "mdi-dumbbell" },
        { id: "stats", name: "Statistiken", icon: "mdi-chart-bar" },
        { id: "edit", name: "Bearbeiten", icon: "mdi-pencil" },
      ],
      articles: [
        // STATISTIKEN
        {
          id: "1rm",
          category: "stats",
          title: "Wie wird mein 1RM berechnet?",
          subtitle: "Verstehe deine Kraftwerte",
          icon: "mdi-calculator",
          color: "#E91E63",
          content: `
            <p>Dein <strong>One Rep Max (1RM)</strong> ist das theoretische Maximalgewicht, das du für eine einzige Wiederholung bewegen könntest.</p>
            <p>Wir nutzen die bewährte <strong>Epley-Formel</strong>:</p>
            <code style="background: rgba(255,255,255,0.1); padding: 4px; border-radius: 4px;">1RM = Gewicht × (1 + (Whd / 30))</code>
            <p class="mt-2">Beispiel: Du schaffst 100kg für 10 Wiederholungen.<br>
            1RM = 100 × (1 + 0.33) ≈ <strong>133kg</strong>.</p>
            <div class="pro-tip">💡 <strong>Tipp:</strong> Diese Formel ist am genausten im Wiederholungsbereich von 1-10.</div>
          `,
        },
        {
          id: "charts",
          category: "stats",
          title: "Trainings-Historie & Charts",
          subtitle: "Deine Fortschritte visualisiert",
          icon: "mdi-chart-line",
          color: "#9C27B0",
          content: `
            <p>Im Reiter "Übungen" siehst du für jede Übung detaillierte Charts:</p>
            <ul>
              <li><strong>Volumen:</strong> Das bewegte Gesamtgewicht (Sätze × Wdh × Gewicht).</li>
              <li><strong>Maximalgewicht:</strong> Das schwerste Gewicht des Tages.</li>
              <li><strong>1RM Trend:</strong> Deine geschätzte Maximalkraft über die Zeit.</li>
            </ul>
            <p class="mt-2">Swipe im Hub nach rechts/links, um zwischen den Diagrammen zu wechseln.</p>
          `,
        },

        // TRAINING
        {
          id: "sets",
          category: "workout",
          title: "Sätze hinzufügen & löschen",
          subtitle: "Passe dein Workout live an",
          icon: "mdi-playlist-plus",
          color: "#2196F3",
          content: `
            <p><strong>Neuer Satz:</strong> Klicke unter der Übung auf <span style="background: #2196F3; color: white; padding: 0 4px; border-radius: 4px;">+ Satz hinzufügen</span>. Wir übernehmen automatisch das Gewicht vom vorherigen Satz.</p>
            <p><strong>Satz löschen:</strong> Tippe einfach auf den entsprechenden Satz. Ein Dialog fragt dich zur Sicherheit, ob du den Satz wirklich löschen möchtest.</p>
          `,
        },
        {
          id: "superset",
          category: "workout",
          title: "Was sind Supersätze?",
          subtitle: "Effizienter trainieren",
          icon: "mdi-link-variant",
          color: "#00BCD4",
          content: `
            <p>Supersätze verbinden zwei Übungen ohne Pause. In deiner Übungsliste erkennst du sie an der farbigen Verbindungslinie am linken Rand.</p>
            <p>Wenn du einen Satz der ersten Übung abhüst, springt die App automatisch zur zweiten Übung des Supersatzes.</p>
          `,
        },

        // GRUNDLAGEN
        {
          id: "start",
          category: "basics",
          title: "Wie starte ich ein Training?",
          subtitle: "Dein erster Schritt",
          icon: "mdi-play-circle",
          color: "#4CAF50",
          content: `
            <p>Im <strong>Hub</strong> (Startseite) siehst du deine Trainingspläne als Karten.</p>
            <ol class="ml-4 mt-2">
              <li>Tippe auf eine Plan-Karte.</li>
              <li>Ein Dialog zeigt dir, welche Muskelgruppen trainiert werden.</li>
              <li>Klicke auf <strong>"Fortsetzen"</strong> (wenn du schon Sessions hattest) oder <strong>"Neu Starten"</strong>.</li>
            </ol>
            <div class="pro-tip">⚡ <strong>Freies Training:</strong> Klicke oben auf "Freies Training", um ein Workout ohne Vorlage zu starten.</div>
          `,
        },
        {
          id: "feedback",
          category: "basics",
          title: "Feedback & Timer nutzen",
          subtitle: "Neue Funktionen aktivieren",
          icon: "mdi-tune",
          color: "#FF9800",
          content: `
            <p>Du kannst dein Trainingserlebnis anpassen:</p>
            <ul>
              <li><strong>Feedback:</strong> Nach jedem Satz kannst du bewerten, wie schwer es war (RPE). Das hilft bei der langfristigen Steuerung.</li>
              <li><strong>Timer:</strong> Startet automatisch nach einem Satz die Pause.</li>
            </ul>
            <p class="mt-2">Gehe im Hub oben rechts auf das ⚙️-Menü, um diese Funktionen an- oder auszuschalten.</p>
          `,
        },

        // BEARBEITEN
        {
          id: "move",
          category: "edit",
          title: "Übungen verschieben",
          subtitle: "Reihenfolge ändern",
          icon: "mdi-drag-vertical",
          color: "#607D8B",
          content: `
            <p>Gehe in die Übungsliste deines Workouts:</p>
            <ol class="ml-4 mt-2">
              <li>Tippe auf die drei Punkte <v-icon small>mdi-dots-vertical</v-icon> neben dem Übungsnamen.</li>
              <li>Wähle <strong>"Nach oben"</strong> oder <strong>"Nach unten"</strong>.</li>
            </ol>
            <p>Deine Änderung wird sofort für das laufende Training gespeichert.</p>
          `,
        },
        {
          id: "edit_exercise",
          category: "edit",
          title: "Übung austauschen",
          subtitle: "Wenn das Gerät besetzt ist",
          icon: "mdi-swap-horizontal",
          color: "#795548",
          content: `
            <p><strong>Bearbeiten:</strong> Tippe auf die drei Punkte <v-icon small>mdi-dots-vertical</v-icon> neben der Übung und wähle <strong>"Bearbeiten"</strong>, um Sätze, Wiederholungen oder Pausenzeiten anzupassen.</p>
            <p class="mt-2"><strong>Austauschen:</strong> Tippe während der Übung oben rechts im Header auf das <strong>Wechsel-Icon</strong> <v-icon small>mdi-swap-horizontal</v-icon>, um die Übung gegen eine andere zu tauschen.</p>
          `,
        },
      ],
    }
  },
  computed: {
    dialog: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit("input", val)
      },
    },
    filteredArticles() {
      let result = this.articles

      // Category Filter
      if (this.selectedCategory !== "all") {
        result = result.filter((a) => a.category === this.selectedCategory)
      }

      // Search Filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        result = result.filter(
          (a) => a.title.toLowerCase().includes(query) || a.subtitle.toLowerCase().includes(query) || a.content.toLowerCase().includes(query),
        )
      }

      return result
    },
  },
  methods: {
    close() {
      this.dialog = false
    },
    toggleArticle(id) {
      if (this.expandedArticle === id) {
        this.expandedArticle = null
      } else {
        this.expandedArticle = id
      }
    },
  },
}
</script>

<style scoped>
.help-center-dialog {
  background: #121212;
}
.help-center-card {
  background: #121212 !important;
  min-height: 100vh;
  position: relative;
  overflow: hidden;
}

/* Aurora Background */
.aurora-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 50vh;
  background: radial-gradient(circle at 50% -20%, rgba(33, 150, 243, 0.15), transparent 70%);
  pointer-events: none;
  z-index: 0;
}

.content-container {
  position: relative;
  z-index: 2;
  padding-bottom: 80px; /* Space for scrolling */
}

/* Glass UI Elements */
.glass-toolbar {
  background: rgba(20, 20, 20, 0.8) !important;
  backdrop-filter: blur(10px);
  position: sticky;
  top: 0;
  z-index: 10;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.glass-search.v-text-field.v-text-field--solo .v-input__control .v-input__slot {
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.gradient-text {
  background: linear-gradient(45deg, #ffffff, #bbbbbb);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* Categories Scroll */
.categories-scroll {
  display: flex;
  overflow-x: auto;
  gap: 12px;
  padding-bottom: 4px;
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.categories-scroll::-webkit-scrollbar {
  display: none;
}
.category-chip {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 8px 16px;
  border-radius: 20px;
  color: #b0b0b0;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
}
.category-chip.active {
  background: rgba(33, 150, 243, 0.2);
  border-color: rgba(33, 150, 243, 0.5);
  color: white;
  transform: scale(1.05);
}

/* Article Cards */
.article-card {
  background: rgba(30, 30, 30, 0.6) !important;
  border-radius: 16px !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(5px);
  transition: all 0.2s ease;
}
.article-card:active {
  transform: scale(0.98);
}
.rotate-180 {
  transform: rotate(180deg);
  transition: transform 0.3s ease;
}

.article-content ul,
.article-content ol {
  padding-left: 20px;
  margin-bottom: 10px;
}
.article-content li {
  margin-bottom: 6px;
}

.pro-tip {
  background: rgba(33, 150, 243, 0.15);
  border-left: 3px solid #2196f3;
  padding: 12px;
  border-radius: 0 8px 8px 0;
  margin-top: 12px;
  font-size: 0.9rem;
  color: #e3f2fd;
}
</style>
